import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  IconButton,
  Input,
  Avatar,
  Badge,
  Spinner,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  InputGroup,
  InputRightElement,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Button,
  Link,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import {
  FiSend,
  FiPaperclip,
  FiMoreVertical,
  FiSearch,
  FiChevronLeft,
  FiMessageSquare,
  FiImage,
  FiFileText,
  FiVideo,
  FiMic,
  FiUser,
  FiInfo,
  FiDownload,
  FiX,
  FiMaximize2,
  FiChevronRight,
  FiSidebar,
  FiShield,
  FiCopy,
  FiExternalLink,
  FiChevronDown,
  FiChevronsDown,
} from "react-icons/fi";

import {
  ref as dbRef,
  onChildAdded,
  off,
  set,
  get,
  onChildRemoved,
  query as rtdbQuery,
  orderByKey,
  limitToLast,
  runTransaction,
  onValue,
  startAt,
  endBefore,
  limitToFirst,
} from "firebase/database";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  getBlob,
  updateMetadata,
} from "firebase/storage";
import { rtdb, db, storage } from "../../firebase/firebase";
import { format, isSameDay, isYesterday } from "date-fns";
import { ChakraIcon } from "../../components/ChakraIcon";
import { showToast } from "../../utils/toastUtils";
import { ResolvedImage, ResolvedVideo, ResolvedAudio } from "../../components/ResolvedMedia";
import { resolveRole } from "../../utils/permissions";
import {
  MessageMeta,
  ChatRowTicks,
  deriveTickState,
  useReceipts,
  usePublishReceipts,
  useMessageOutbox,
} from "../../components/chat/messageStatus";

interface Message {
  id: string;
  text?: string;
  userId: string;
  createdAt: number;
  type: "text" | "image" | "video" | "document" | "audio";
  fileUrl?: string;
  url?: string;
  fileName?: string;
  locationName?: string;
  caption?: string;
}

interface DirectChatUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  lastMessage?: {
    text: string;
    time: number;
    type: string;
    userId?: string;
  };
  unreadCount?: number;
}

interface MyChatsMainProps {
  currentUser: any;
  organizationId: string;
  users: any[];
}

const buildConvId = (a: string, b: string) => [a, b].sort().join("_");

const MyChatsMain: React.FC<MyChatsMainProps> = ({
  currentUser,
  organizationId,
  users: allUsers,
}) => {
  const [selectedUser, setSelectedUser] = useState<DirectChatUser | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState("");
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [contactList, setContactList] = useState<DirectChatUser[]>([]);
  const [isMessageSearchOpen, setIsMessageSearchOpen] = useState(false);
  const [messageSearchText, setMessageSearchText] = useState("");
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [viewerMedia, setViewerMedia] = useState<{ url: string; type: string; name: string } | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [firstMessageKey, setFirstMessageKey] = useState<string | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const scrollRef = useRef<HTMLDivElement>(null);
  const initialScrollDoneRef = useRef(false);


  const cardBg = useColorModeValue("white", "#1A1C1F");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const sidebarBg = useColorModeValue("gray.50", "#14161B");
  const activeChatBg = useColorModeValue("blue.50", "whiteAlpha.100");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedTextColor = useColorModeValue("gray.500", "gray.400");
  const chatBg = useColorModeValue("#F0F2F5", "#0B0B0F");
  const searchInputBg = useColorModeValue("white", "gray.800");
  const messageMeBg = useColorModeValue("gray.100", "gray.700"); // Light gray for me
  const messageMeTextColor = useColorModeValue("gray.800", "white");
  const messageOtherBg = useColorModeValue("white", "gray.800"); 
  const dateBadgeBg = useColorModeValue("white", "gray.700");
  const inputBg = useColorModeValue("gray.100", "gray.800");
  const inputHoverBg = useColorModeValue("gray.200", "gray.700");
  const inputFocusBg = useColorModeValue("white", "gray.700");
  const scrollBtnBg = useColorModeValue("gray.100", "gray.700");
  const scrollBtnColor = useColorModeValue("gray.600", "gray.200");
  const scrollBtnHoverBg = useColorModeValue("gray.200", "gray.600");

  // Load and sync unread counts and last messages for each user
  useEffect(() => {
    if (!organizationId || !currentUser) return;

    // Admin = permission "Admin", role "admin" or the organization's admin (org admin).
    // Admins can chat with every user in the organization.
    const isAdmin = resolveRole(currentUser).isAdmin;
    const myChannels = Object.values(currentUser.assignedChannels || {}).flat();

    const otherUsers = allUsers
      .filter((u) => {
        if (u.id === currentUser.id) return false;
        if (isAdmin) return true; // Admins see everyone
        
        // Filter: common channel check
        const theirChannels = Object.values(u.assignedChannels || {}).flat();
        const hasCommonChannel = myChannels.some(chId => theirChannels.includes(chId));
        
        if (hasCommonChannel) return true;

        // If current user has NO channels, allow them to see Managers/Admins
        if (myChannels.length === 0) {
          const theirRole = u.role?.toLowerCase() || u.permissions?.toLowerCase() || "";
          return theirRole.includes("manager") || theirRole.includes("admin");
        }

        return false;
      })
      .map((u) => ({
        id: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        role: u.role || u.permissions,
        email: u.email,
        unreadCount: 0,
      }));

    setContactList(otherUsers);

    const unsubscribes: (() => void)[] = [];

    otherUsers.forEach((user) => {
      const conversationId = buildConvId(currentUser.id, user.id);
      const metaRef = dbRef(
        rtdb,
        `organization/${organizationId}/users/${conversationId}/metadata`
      );

      const rtdbMetaUnsub = onValue(metaRef, (snapshot) => {
        const data = snapshot.val();
        console.log(`[MyChats] RTDB Meta for ${user.firstName}:`, data); // Debug Log
        if (data) {
          // Robust extraction: Check root and nested 'metadata' object
          const unread = data.userUnreadCounts?.[currentUser.id] ?? 
                         data.metadata?.userUnreadCounts?.[currentUser.id] ?? 
                         data.unreadCount ?? 
                         data.metadata?.unreadCount;
          
          const lastMsgData = data.lastMessage ?? data.lastMsg ?? data.metadata?.lastMessage ?? data.metadata?.lastMsg;
          
          // Enhanced fallback: if metadata exists but last message is missing, try to fetch it from the messages node
          const updateContact = (lastMsg: any) => {
            setContactList((prev) =>
              prev.map((u) => {
                if (u.id === user.id) {
                  return {
                    ...u,
                    unreadCount: unread ?? u.unreadCount ?? 0,
                    lastMessage: lastMsg || u.lastMessage,
                  };
                }
                return u;
              })
            );
          };

          if (!lastMsgData) {
            const lastMsgQuery = rtdbQuery(dbRef(rtdb, `organization/${organizationId}/users/${conversationId}/messages`), orderByKey(), limitToLast(1));
            get(lastMsgQuery).then(snap => {
              if (snap.exists()) {
                const val = snap.val();
                const key = Object.keys(val)[0];
                const msg = val[key];
                updateContact({ text: msg.text || (msg.type === 'image' ? 'Image' : 'File'), time: msg.createdAt, type: msg.type });
              } else {
                updateContact(null);
              }
            });
          } else {
            updateContact(lastMsgData);
          }
        }
      });
      unsubscribes.push(rtdbMetaUnsub);

      // Also listen on Firestore for mirror data
      const firestoreUnsub = onSnapshot(
        doc(db, `organization/${organizationId}/users/${conversationId}`) as any,
        (doc: any) => {
           if(doc.exists()){
             const data = doc.data();
             console.log(`[MyChats] Firestore Meta for ${user.firstName}:`, data); // Debug Log
             
             // Robust extraction for Firestore
             const lastMsgData = data.lastMessage ?? data.lastMsg ?? data.metadata?.lastMessage ?? data.metadata?.lastMsg;
             const unread = data.userUnreadCounts?.[currentUser.id] ?? 
                            data.metadata?.userUnreadCounts?.[currentUser.id] ?? 
                            data.unreadCount ?? 
                            data.metadata?.unreadCount;
             
             // Handle Firestore Timestamp conversion
             if (lastMsgData && lastMsgData.time && typeof lastMsgData.time === 'object' && 'seconds' in lastMsgData.time) {
               lastMsgData.time = lastMsgData.time.seconds * 1000;
             }

             setContactList((prev) =>
              prev.map((u) => {
                if (u.id === user.id) {
                  return {
                    ...u,
                    unreadCount: unread ?? u.unreadCount ?? 0,
                    lastMessage: lastMsgData || u.lastMessage,
                  };
                }
                return u;
              })
            );
           }
        }
      );
      unsubscribes.push(firestoreUnsub);
      
      // Use RTDB for faster last message updates if needed, but Firestore metadata is what the mobile app uses
    });

    return () => unsubscribes.forEach((unsub) => unsub());
  }, [organizationId, currentUser, allUsers]);

  /* ---------------------------------------------------------------------- */
  /*  DELIVERY STATUS + READ RECEIPTS + RETRYABLE SENDS                      */
  /* ---------------------------------------------------------------------- */
  const convBasePath = useMemo(
    () =>
      organizationId && currentUser && selectedUser
        ? `organization/${organizationId}/users/${buildConvId(currentUser.id, selectedUser.id)}`
        : null,
    [organizationId, currentUser, selectedUser]
  );
  const messagesPath = convBasePath ? `${convBasePath}/messages` : null;

  // Retryable sends for every message this screen writes.
  const outbox = useMessageOutbox(messagesPath);

  // Both halves of the receipt exchange: what the other person has seen, and
  // publishing what I have seen.
  const receipts = useReceipts(convBasePath);
  const newestTs = useMemo(
    () => messages.reduce((t, m) => (m.createdAt > t ? m.createdAt : t), 0),
    [messages]
  );
  usePublishReceipts(convBasePath, currentUser?.id, newestTs, true);

  // A direct chat has exactly one reader, so "they read it" is unambiguous —
  // this is the module that earns the blue double tick.
  const peerUids = useMemo(() => (selectedUser ? [selectedUser.id] : []), [selectedUser]);
  const tickStateFor = useCallback(
    (m: Message) =>
      deriveTickState({
        sendState: outbox.entries[m.id]?.state,
        createdAt: m.createdAt,
        receipts,
        peerUids,
        trackRead: true,
      }),
    [outbox.entries, receipts, peerUids]
  );

  // An unacknowledged send is normally already in `messages` — the Realtime
  // Database echoes a local write before the server sees it. It is only
  // missing when the write was rejected and rolled back, which is exactly
  // when the user needs it kept on screen with a retry affordance.
  const displayMessages = useMemo(() => {
    const known = new Set(messages.map((m) => m.id));
    const stranded = Object.values(outbox.entries)
      .filter((e) => !known.has(e.id))
      .map((e) => ({ id: e.id, ...e.msg } as Message));
    return [...messages, ...stranded].sort((a, b) => a.createdAt - b.createdAt);
  }, [messages, outbox.entries]);

  // Scroll to bottom helper
  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior
      });
      setIsAtBottom(true);
      setShowScrollButton(false);
    }
  };

  // Handle manual scroll
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 100;
    setIsAtBottom(isBottom);
    setShowScrollButton(!isBottom);
  };

  // Load messages when a user is selected
  useEffect(() => {
    if (!selectedUser || !organizationId || !currentUser?.id) return;

    const conversationId = buildConvId(currentUser.id, selectedUser.id);
    const messagesPath = `organization/${organizationId}/users/${conversationId}/messages`;
    const messagesRef = dbRef(rtdb, messagesPath);
    const messagesQuery = rtdbQuery(messagesRef, orderByKey(), limitToLast(25));

    setIsLoadingMessages(true);
    setMessages([]);
    setHasMoreMessages(true);
    setFirstMessageKey(null);

    const handleNewMessage = (snapshot: any) => {
      const msg = snapshot.val();
      const newMessage = { id: snapshot.key, ...msg };
      
      setMessages(prev => {
        const exists = prev.some(m => m.id === newMessage.id);
        if (exists) return prev;
        const updated = [...prev, newMessage];
        return updated.sort((a, b) => a.createdAt - b.createdAt);
      });
      
      // Auto-scroll logic if already at bottom or it's my message
      if (initialScrollDoneRef.current && (isAtBottom || newMessage.userId === currentUser.id)) {
        setTimeout(() => scrollToBottom("smooth"), 100);
      }
    };

    const handleRemovedMessage = (snapshot: any) => {
      setMessages((prev) => prev.filter((m) => m.id !== snapshot.key));
    };

    onChildAdded(dbRef(rtdb, messagesPath), (snapshot) => {
      if (!firstMessageKey) setFirstMessageKey(snapshot.key);
    });

    onChildAdded(messagesQuery, handleNewMessage);
    onChildRemoved(messagesRef, handleRemovedMessage);

    // Initial load check to stop spinner even if no messages
    onValue(messagesQuery, (snapshot) => {
      setIsLoadingMessages(false);
      if (snapshot.exists()) {
        const keys = Object.keys(snapshot.val());
        setFirstMessageKey(keys[0]);
        setHasMoreMessages(keys.length === 25);
      }
    }, { onlyOnce: true });

    // Mark as read
    const markAsRead = async () => {
      const metaRef = dbRef(
        rtdb,
        `organization/${organizationId}/users/${conversationId}/metadata/userUnreadCounts/${currentUser.id}`
      );
      await set(metaRef, 0);
      
      // Also update Firestore if the app uses it for metadata
      const firestoreMetaRef = doc(db, `organization/${organizationId}/users/${conversationId}`);
      await setDoc(firestoreMetaRef, {
        userUnreadCounts: {
          [currentUser.id]: 0
        }
      }, { merge: true }).catch(() => {});
    };
    markAsRead();

    return () => {
      off(messagesQuery, "child_added", handleNewMessage);
      off(messagesRef, "child_removed", handleRemovedMessage);
    };
  }, [selectedUser, organizationId, currentUser?.id]);

  // Reset scroll completion state when user changes
  useEffect(() => {
    initialScrollDoneRef.current = false;
  }, [selectedUser?.id]);

  // Scroll to bottom once initial messages are loaded
  useEffect(() => {
    if (!isLoadingMessages && messages.length > 0 && !initialScrollDoneRef.current) {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
      initialScrollDoneRef.current = true;
    }
  }, [messages, isLoadingMessages]);

  const handleDownload = async (url: string, fileName: string) => {
    try {
      const storageReference = storageRef(storage, url);
      
      // 1. Force attachment metadata
      await updateMetadata(storageReference, {
        contentDisposition: `attachment; filename="${fileName}"`,
      });

      // 2. Refresh URL with a cache-buster
      const baseDirectUrl = await getDownloadURL(storageReference);
      const directUrl = `${baseDirectUrl}&t=${Date.now()}`;
      
      // 3. Use an iframe to trigger the download WITHOUT navigating
      let iframe = document.getElementById('hidden-download-frame') as HTMLIFrameElement;
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'hidden-download-frame';
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
      }
      iframe.src = directUrl;
    } catch (error: any) {
      console.error("Download failed:", error);
      showToast({
        title: "Opening in viewer...",
        description: "Native save was blocked. Please right-click the file in the new tab and select 'Save As'.",
        status: "warning",
        duration: 6000,
        isClosable: true,
      });
      window.open(url, '_blank');
    }
  };

  const loadMoreMessages = useCallback(async () => {
    if (!firstMessageKey || !selectedUser || !organizationId || !currentUser) return;

    const conversationId = buildConvId(currentUser.id, selectedUser.id);
    const path = `organization/${organizationId}/users/${conversationId}/messages`;
    const messagesRef = dbRef(rtdb, path);
    const q = rtdbQuery(messagesRef, orderByKey(), endBefore(firstMessageKey), limitToLast(30));

    const container = scrollRef.current;
    const prevScrollHeight = container ? container.scrollHeight : 0;
    const prevScrollTop = container ? container.scrollTop : 0;

    const snapshot = await get(q);
    if (snapshot.exists()) {
      const val = snapshot.val();
      const newMessages = Object.keys(val).map(key => ({ id: key, ...val[key] })) as Message[];
      setMessages(prev => [...newMessages, ...prev].sort((a, b) => a.createdAt - b.createdAt));
      const keys = Object.keys(val);
      setFirstMessageKey(keys[0]);
      setHasMoreMessages(keys.length === 30);

      // Pin the scroll position so it doesn't jump
      if (container) {
        setTimeout(() => {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
        }, 50);
      }
    } else {
      setHasMoreMessages(false);
    }
  }, [selectedUser, organizationId, currentUser, firstMessageKey]);

  const sendMessage = async () => {
    if (!inputText.trim() || !selectedUser || !currentUser) return;

    const conversationId = buildConvId(currentUser.id, selectedUser.id);
    const trimmedText = inputText.trim();
    setInputText("");

    const syncMetadata = async () => {
      try {
        // Update last message and unread counts in metadata (Firestore)
        const metaRef = doc(db, `organization/${organizationId}/users/${conversationId}`);

        const metaSnap = await getDoc(metaRef);
        const currentUnread = metaSnap.exists() ? (metaSnap.data().userUnreadCounts?.[selectedUser.id] || 0) : 0;

        const currentLastMsg = {
          text: trimmedText,
          time: Date.now(),
          type: "text",
          userId: currentUser.id,
        };

        await setDoc(metaRef, {
          lastMessage: currentLastMsg,
          userUnreadCounts: {
            [selectedUser.id]: currentUnread + 1
          },
          participants: [currentUser.id, selectedUser.id],
          organizationId
        }, { merge: true });

        // Update RTDB Metadata for instant sync
        const rtdbMetaRef = dbRef(rtdb, `organization/${organizationId}/users/${conversationId}/metadata`);
        await set(rtdbMetaRef, {
          lastMessage: currentLastMsg,
          userUnreadCounts: {
            [selectedUser.id]: currentUnread + 1
          }
        });
      } catch (error) {
        console.error("Error updating chat metadata:", error);
      }
    };

    // Retryable: outbox keeps the message visible with a "sending" clock and
    // flips it to a tap-to-retry state if the write times out or is rejected,
    // instead of silently failing on a dead/slow connection.
    await outbox.send(
      () => ({
        text: trimmedText,
        createdAt: Date.now(),
        userId: currentUser.id,
        type: "text",
      }),
      syncMetadata
    );
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = event.target.files?.[0];
    if (!file || !selectedUser || !currentUser) return;

    setIsUploading(true);
    const conversationId = buildConvId(currentUser.id, selectedUser.id);
    const fileName = `${Date.now()}_${file.name}`;
    const sRef = storageRef(storage, `uploads/${conversationId}/${fileName}`);
    const uploadTask = uploadBytesResumable(sRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        console.error("Upload error:", error);
        setIsUploading(false);
        showToast({ title: "Upload Failed", status: "error" });
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

        // The upload already succeeded; only the message node is left, so a
        // failure here is retryable without re-uploading the file.
        await outbox.send(
          () => ({
            createdAt: Date.now(),
            userId: currentUser.id,
            type: type === "image" ? "image" : type === "video" ? "video" : "document",
            fileUrl: downloadURL,
            fileName: file.name,
          }),
          async () => {
            try {
              const metaRef = doc(db, `organization/${organizationId}/users/${conversationId}`);
              const metaSnap = await getDoc(metaRef);
              const currentUnread = metaSnap.exists() ? (metaSnap.data().userUnreadCounts?.[selectedUser.id] || 0) : 0;

              const currentLastMsg = {
                text: type.charAt(0).toUpperCase() + type.slice(1),
                time: Date.now(),
                type,
                userId: currentUser.id,
              };

              await setDoc(metaRef, {
                lastMessage: currentLastMsg,
                userUnreadCounts: {
                  [selectedUser.id]: currentUnread + 1
                }
              }, { merge: true });

              // Update RTDB Metadata for instant sync
              const rtdbMetaRef = dbRef(rtdb, `organization/${organizationId}/users/${conversationId}/metadata`);
              await set(rtdbMetaRef, {
                lastMessage: currentLastMsg,
                userUnreadCounts: {
                  [selectedUser.id]: currentUnread + 1
                }
              });
            } catch (error) {
              console.error("Error updating chat metadata:", error);
            }
          }
        );

        setIsUploading(false);
        setUploadProgress(0);
      }
    );
  };

  const formatLastMessageTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    
    if (isSameDay(date, now)) {
      return date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).toLowerCase();
    }
    
    if (isYesterday(date)) {
      return "Yesterday";
    }
    
    return date.toLocaleDateString('en-GB'); // dd/mm/yyyy
  };

  const renderMessageText = (text: string) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, i) => {
      if (part.match(urlRegex)) {
        return (
          <Link 
            key={i} 
            href={part} 
            isExternal 
            color="blue.500" 
            textDecoration="underline" 
            _hover={{ color: "blue.600" }}
          >
            {part}
          </Link>
        );
      }
      return part;
    });
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).toLowerCase();
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    if (isSameDay(date, now)) return "Today";
    if (isYesterday(date)) return "Yesterday";
    return date.toLocaleDateString([], {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "?";
  };

  const filteredContacts = contactList
    .filter((u) =>
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const timeA = a.lastMessage?.time || 0;
      const timeB = b.lastMessage?.time || 0;
      return timeB - timeA;
    });

  return (
    <Flex h="calc(100vh - 50px)" bg={cardBg} borderRadius="0" overflow="hidden" border="1px solid" borderColor={borderColor}>
      {/* Users Sidebar */}
      <Box 
        w={{ base: "full", md: isSidebarVisible ? "350px" : "0px" }} 
        bg={sidebarBg} 
        borderRight={isSidebarVisible ? "1px solid" : "none"} 
        borderColor={borderColor} 
        display={{ base: selectedUser ? "none" : "block", md: isSidebarVisible ? "block" : "none" }}
        transition="all 0.3s ease"
        overflow="hidden"
      >
        <VStack h="full" spacing={0} align="stretch">
          <Box p={4}>
            <Flex align="center" justify="space-between" mb={4}>
              <Text fontSize="xl" fontWeight="bold" color={textColor}>My Chats</Text>
              {selectedUser && (
                <IconButton
                  aria-label="Close Sidebar"
                  icon={<ChakraIcon as={FiChevronLeft} />}
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsSidebarVisible(false)}
                  display={{ base: "none", md: "flex" }}
                />
              )}
            </Flex>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <ChakraIcon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search users..."
                borderRadius="full"
                bg={searchInputBg}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </Box>

          <Box flex={1} overflowY="auto" sx={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { background: 'gray.300', borderRadius: '4px' }
          }}>
            {filteredContacts.length === 0 ? (
              <Flex p={8} justify="center" align="center" direction="column">
                <Text color={mutedTextColor} fontSize="sm">No users found</Text>
              </Flex>
            ) : (
              filteredContacts.map((user) => (
                  <Flex
                    key={user.id}
                    p={3}
                    align="center"
                    cursor="pointer"
                    transition="all 0.2s"
                    bg={selectedUser?.id === user.id ? activeChatBg : "transparent"}
                    _hover={{ bg: activeChatBg }}
                    onClick={() => setSelectedUser(user)}
                  >
                    <Flex
                      w="32px" h="32px"
                      borderRadius="full"
                      bg="transparent"
                      border="1px solid"
                      borderColor="zs.accent"
                      color="zs.accent"
                      mr={3}
                      align="center"
                      justify="center"
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      {getInitials(user.firstName, user.lastName)}
                    </Flex>
                    <VStack align="start" spacing={0} flex={1} overflow="hidden">
                      <HStack w="full" justify="space-between" align="baseline">
                        <Text fontWeight="semibold" fontSize="sm" color={textColor} isTruncated flex={1}>
                          {user.firstName} {user.lastName}
                        </Text>
                        {user.lastMessage && (
                          <Text fontSize="10px" color={mutedTextColor} ml={2} whiteSpace="nowrap">
                            {formatLastMessageTime(user.lastMessage.time)}
                          </Text>
                        )}
                      </HStack>
                      <HStack spacing="3px" w="full">
                        {user.lastMessage && user.lastMessage.userId === currentUser.id && (
                          <ChatRowTicks
                            basePath={`organization/${organizationId}/users/${buildConvId(currentUser.id, user.id)}`}
                            myUid={currentUser.id}
                            peerUid={user.id}
                            createdAt={user.lastMessage.time}
                          />
                        )}
                        <Text fontSize="xs" color={mutedTextColor} isTruncated flex={1}>
                          {user.lastMessage ? user.lastMessage.text : "Tap to chat"}
                        </Text>
                      </HStack>
                    </VStack>
                    <VStack align="end" spacing={1} ml={2}>
                      {user.unreadCount > 0 && (
                        <Badge colorScheme="red" borderRadius="full" px={1.5} minW="18px" h="18px" display="flex" alignItems="center" justifyContent="center" variant="solid" fontSize="10px">
                          {user.unreadCount}
                        </Badge>
                      )}
                    </VStack>
                  </Flex>
              ))
            )}
          </Box>
        </VStack>
      </Box>

      {/* Main Chat Area */}
      <Flex flex={1} direction="column" display={{ base: selectedUser ? "flex" : "none", md: "flex" }} bg={chatBg}>
        {selectedUser ? (
          <>
            {/* Header */}
            <Flex p={3} align="center" justify="space-between" bg={cardBg} borderBottom="1px solid" borderColor={borderColor} boxShadow="sm" zIndex={1}>
              <HStack spacing={3} flex={1}>
                {!isSidebarVisible && (
                  <IconButton
                    aria-label="Open Sidebar"
                    icon={<ChakraIcon as={FiChevronRight} />}
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsSidebarVisible(true)}
                    display={{ base: "none", md: "flex" }}
                  />
                )}
                <IconButton
                  display={{ base: "flex", md: "none" }}
                  aria-label="Back"
                  icon={<ChakraIcon as={FiChevronLeft} />}
                  variant="ghost"
                  onClick={() => setSelectedUser(null)}
                />
                <Flex
                  w="32px" h="32px"
                  borderRadius="full"
                  bg="transparent"
                  border="1px solid"
                  borderColor="zs.accent"
                  color="zs.accent"
                  align="center"
                  justify="center"
                  fontSize="xs"
                  fontWeight="bold"
                >
                  {getInitials(selectedUser.firstName, selectedUser.lastName)}
                </Flex>
                <VStack align="start" spacing={0}>
                  <Text fontWeight="bold" fontSize="md" color={textColor}>
                    {selectedUser.firstName} {selectedUser.lastName}
                  </Text>
                  <Text fontSize="xs" color={mutedTextColor}>Active now</Text>
                </VStack>
              </HStack>
              <HStack ml={2} spacing={1}>
                {isMessageSearchOpen && (
                  <InputGroup size="sm" w="200px">
                    <Input
                      placeholder="Search..."
                      borderRadius="full"
                      bg={searchInputBg}
                      value={messageSearchText}
                      onChange={(e) => setMessageSearchText(e.target.value)}
                      autoFocus
                      pr="2rem"
                    />
                    <InputRightElement width="2rem">
                      <IconButton
                        aria-label="Close search"
                        icon={<ChakraIcon as={FiChevronLeft} />}
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          setIsMessageSearchOpen(false);
                          setMessageSearchText("");
                        }}
                      />
                    </InputRightElement>
                  </InputGroup>
                )}
                <IconButton 
                  aria-label="Search" 
                  icon={<ChakraIcon as={FiSearch} />} 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setIsMessageSearchOpen(!isMessageSearchOpen)}
                  color={isMessageSearchOpen ? "zs.accent" : "inherit"}
                />
                <IconButton aria-label="More" icon={<ChakraIcon as={FiMoreVertical} />} variant="ghost" size="sm" />
              </HStack>
            </Flex>

            {/* Messages List Wrapper */}
            <Box flex={1} position="relative" display="flex" flexDirection="column" minH={0}>
              {/* Messages List */}
              <Box flex={1} p={4} overflowY="auto" ref={scrollRef} onScroll={handleScroll} sx={{
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: '4px' }
              }}>
                {isLoadingMessages ? (
                <Flex h="full" align="center" justify="center"><Spinner color="zs.accent" /></Flex>
              ) : displayMessages.length === 0 ? (
                <Flex h="full" align="center" justify="center" direction="column">
                  <ChakraIcon as={FiMessageSquare} boxSize={12} color="gray.300" mb={4} />
                  <Text color={mutedTextColor}>Start a conversation with {selectedUser.firstName}</Text>
                </Flex>
              ) : (
                <>
                  {hasMoreMessages && (
                    <Flex justify="center" mb={4}>
                      <Button size="xs" variant="ghost" onClick={loadMoreMessages} colorScheme="blue">
                        Load Earlier Messages
                      </Button>
                    </Flex>
                  )}
                  {displayMessages
                    .filter(msg => !messageSearchText || (msg.text && msg.text.toLowerCase().includes(messageSearchText.toLowerCase())))
                    .map((msg, index, filteredArr) => {
                    const isMe = msg.userId === currentUser.id;
                    const showDate = index === 0 || !isSameDay(new Date(msg.createdAt), new Date(filteredArr[index - 1].createdAt));

                    return (
                      <React.Fragment key={msg.id}>
                        {showDate && (
                          <Flex justify="center" my={4}>
                            <Badge bg={dateBadgeBg} px={3} py={1} borderRadius="md" color={mutedTextColor} shadow="sm">
                              {formatDate(msg.createdAt)}
                            </Badge>
                          </Flex>
                        )}
                        <Flex justify={isMe ? "flex-end" : "flex-start"} mb={2} px={{ base: 2, md: 4 }}>
                          <VStack align={isMe ? "flex-end" : "flex-start"} spacing={1} maxW="70%">
                            <Box
                               bg={isMe ? messageMeBg : messageOtherBg}
                               color={isMe ? messageMeTextColor : textColor}
                               px={3}
                               py={2}
                               borderRadius="lg"
                               borderTopRightRadius={isMe ? 0 : "lg"}
                               borderTopLeftRadius={isMe ? "lg" : 0}
                               boxShadow="sm"
                               position="relative"
                               border="1px solid"
                               borderColor={isMe ? "transparent" : borderColor}
                               cursor={["image", "video", "document", "audio"].includes(msg.type) ? "pointer" : "default"}
                               onClick={() => {
                                 if (["image", "video", "document", "audio"].includes(msg.type)) {
                                   setViewerMedia({ url: (msg.fileUrl || msg.url || msg.text)!, type: msg.type, name: msg.fileName || "Audio" });
                                 }
                               }}
                             >
                               {msg.type === "text" && <Text fontSize="sm" whiteSpace="pre-wrap">{renderMessageText(msg.text!)}</Text>}
                               {msg.type === "image" && (
                                 <Box borderRadius="md" overflow="hidden">
                                   <ResolvedImage 
                                     urlOrPath={(msg.fileUrl || msg.url || msg.text)!} 
                                     alt={msg.fileName} 
                                     style={{ maxWidth: '100%', maxHeight: '300px', display: 'block' }} 
                                   />
                                   {msg.caption && <Text fontSize="xs" mt={1}>{msg.caption}</Text>}
                                 </Box>
                               )}
                               {msg.type === "video" && (
                                 <Box borderRadius="md" overflow="hidden" position="relative">
                                   <ResolvedVideo urlOrPath={(msg.fileUrl || msg.url || msg.text)!} style={{ maxWidth: '100%', maxHeight: '300px' }} />
                                   <Flex justify="center" position="absolute" top="0" left="0" w="full" h="full" align="center" bg="blackAlpha.300">
                                     <ChakraIcon as={FiMaximize2} color="white" boxSize={8} />
                                   </Flex>
                                   {msg.caption && <Text fontSize="xs" mt={1}>{msg.caption}</Text>}
                                 </Box>
                               )}
                               {msg.type === "audio" && (
                                  <Box borderRadius="md" py={2} w="280px" maxW="100%">
                                    <ResolvedAudio 
                                      urlOrPath={(msg.fileUrl || msg.url || msg.text)!} 
                                      controls 
                                      style={{ width: '100%' }}
                                    />
                                    {msg.caption && <Text fontSize="xs" mt={1}>{msg.caption}</Text>}
                                  </Box>
                                )}
                                {msg.type === "document" && (
                                 <HStack bg="blackAlpha.100" p={2} borderRadius="md">
                                   <ChakraIcon as={FiFileText} />
                                   <Text fontSize="xs" isTruncated maxW="150px">{msg.fileName}</Text>
                                   <ChakraIcon as={FiDownload} ml="auto" />
                                 </HStack>
                               )}
                              <MessageMeta
                                timeLabel={formatTime(msg.createdAt)}
                                state={isMe ? tickStateFor(msg) : null}
                                tint={isMe ? messageMeTextColor : mutedTextColor}
                                onRetry={() => outbox.retry(msg.id)}
                                onDiscard={() => outbox.discard(msg.id)}
                              />
                            </Box>
                          </VStack>
                        </Flex>
                      </React.Fragment>
                    );
                  })
                  }
                </>
              )}
            </Box>

            {showScrollButton && (
              <IconButton
                aria-label="Scroll to bottom"
                icon={<ChakraIcon as={FiChevronsDown} />}
                position="absolute"
                bottom="20px"
                right="20px"
                zIndex={20}
                bg={scrollBtnBg}
                color={scrollBtnColor}
                _hover={{ bg: scrollBtnHoverBg }}
                borderRadius="full"
                size="md"
                boxShadow="lg"
                onClick={() => scrollToBottom("smooth")}
              />
            )}
          </Box>

            {/* Input Area */}
            <Box p={3} bg={cardBg} borderTop="1px solid" borderColor={borderColor}>
              <HStack spacing={2} align="flex-end">
                <Menu>
                  <MenuButton as={IconButton} aria-label="Attachments" icon={<ChakraIcon as={FiPaperclip} />} variant="ghost" borderRadius="full" />
                  <MenuList>
                    <MenuItem icon={<ChakraIcon as={FiImage} />} onClick={() => document.getElementById('direct-image-upload')?.click()}>Image</MenuItem>
                    <MenuItem icon={<ChakraIcon as={FiVideo} />} onClick={() => document.getElementById('direct-video-upload')?.click()}>Video</MenuItem>
                    <MenuItem icon={<ChakraIcon as={FiFileText} />} onClick={() => document.getElementById('direct-file-upload')?.click()}>Document</MenuItem>
                  </MenuList>
                </Menu>

                <input type="file" id="direct-image-upload" hidden accept="image/*" onChange={(e) => handleFileUpload(e, "image")} />
                <input type="file" id="direct-video-upload" hidden accept="video/*" onChange={(e) => handleFileUpload(e, "video")} />
                <input type="file" id="direct-file-upload" hidden onChange={(e) => handleFileUpload(e, "document")} />

                <Box flex={1} position="relative">
                  {isUploading && (
                    <Box position="absolute" top="-4px" left={0} w={`${uploadProgress}%`} h="2px" bg="zs.accent" transition="width 0.2s" />
                  )}
                  <Input
                    placeholder="Type a message..."
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    variant="filled"
                    bg={inputBg}
                    _hover={{ bg: inputHoverBg }}
                    _focus={{ bg: inputFocusBg, borderColor: "zs.accent" }}
                    borderRadius="xl"
                  />
                </Box>

                {inputText.trim() ? (
                  <IconButton
                    aria-label="Send"
                    icon={<ChakraIcon as={FiSend} />}
                    colorScheme="blue"
                    bg="zs.accent"
                    _hover={{ bg: "zs.accentLight" }}
                    borderRadius="full"
                    onClick={sendMessage}
                  />
                ) : (
                  <IconButton aria-label="Voice" icon={<ChakraIcon as={FiMic} />} variant="ghost" borderRadius="full" />
                )}
              </HStack>
            </Box>
          </>
        ) : (
          <Flex h="full" align="center" justify="center" direction="column">
            <Text fontSize="2xl" fontWeight="bold" color={textColor}>Your Direct Messages</Text>
            <Text color={mutedTextColor}>Select a colleague from the list to start a private conversation</Text>
          </Flex>
        )}
      </Flex>
      {/* Media Viewer Modal */}
      <Modal isOpen={!!viewerMedia} onClose={() => setViewerMedia(null)} size="full" isCentered>
        <ModalOverlay bg="blackAlpha.900" backdropFilter="blur(10px)" />
        <ModalContent bg="transparent" boxShadow="none" m={0}>
          <HStack position="absolute" top={4} right={4} zIndex={20} spacing={4}>
            <IconButton
              aria-label="Download"
              icon={<ChakraIcon as={FiDownload} />}
              onClick={() => viewerMedia && handleDownload(viewerMedia.url, viewerMedia.name)}
              colorScheme="whiteAlpha"
              color="white"
              variant="solid"
              borderRadius="full"
              _hover={{ bg: "whiteAlpha.400" }}
            />
            <IconButton 
              aria-label="Close" 
              icon={<ChakraIcon as={FiX} />} 
              onClick={() => setViewerMedia(null)} 
              colorScheme="whiteAlpha"
              color="white"
              variant="solid"
              borderRadius="full"
              _hover={{ bg: "whiteAlpha.400" }}
            />
          </HStack>
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center" bg="transparent">
            <Flex direction="column" align="center" justify="center" w="full" h="100vh" p={4}>
              {viewerMedia?.type === "image" && (
                <ResolvedImage urlOrPath={viewerMedia.url} alt="View" style={{ maxHeight: "90vh", maxWidth: "90vw", borderRadius: "8px", objectFit: "contain" }} />
              )}
              {viewerMedia?.type === "video" && (
                <ResolvedVideo urlOrPath={viewerMedia.url} controls autoPlay style={{ maxHeight: "90vh", maxWidth: "90vw", borderRadius: "8px" }} />
              )}
              {viewerMedia?.type === "audio" && (
                <Flex direction="column" align="center" bg={cardBg} p={10} borderRadius="lg" shadow="2xl" w="350px" maxW="90%">
                  <ChakraIcon as={FiMic} boxSize={20} color="zs.accent" mb={6} />
                  <Text fontWeight="bold" mb={6} textAlign="center">{viewerMedia.name}</Text>
                  <ResolvedAudio urlOrPath={viewerMedia.url} controls style={{ width: "100%" }} />
                  <Button 
                    mt={6}
                    onClick={() => viewerMedia && handleDownload(viewerMedia.url, viewerMedia.name)}
                    leftIcon={<ChakraIcon as={FiDownload} />} 
                    colorScheme="blue" 
                    bg="zs.accent"
                    _hover={{ bg: "zs.accentLight" }}
                  >
                    Download Audio
                  </Button>
                </Flex>
              )}
              {viewerMedia?.type === "document" && (
                <Flex direction="column" align="center" bg={cardBg} p={10} borderRadius="lg" shadow="2xl" maxW="400px">
                  <ChakraIcon as={FiFileText} boxSize={20} color="zs.accent" mb={6} />
                  <Text fontWeight="bold" mb={6} textAlign="center">{viewerMedia.name}</Text>
                  <Button 
                    onClick={() => viewerMedia && handleDownload(viewerMedia.url, viewerMedia.name)}
                    leftIcon={<ChakraIcon as={FiDownload} />} 
                    colorScheme="blue" 
                    bg="zs.accent"
                    _hover={{ bg: "zs.accentLight" }}
                  >
                    Download File
                  </Button>
                </Flex>
              )}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default MyChatsMain;
